import { ReactElement } from 'react';
import { creatorbase } from '@round/api';
import { Button, LibCombobox, lucideIcons } from '@round/creatorbase-ui';
import PoolOption from '../PoolOption/PoolOption';
import { PoolItem, usePoolSearchCombobox } from '../usePoolSearchCombobox';
import styles from './AddToPoolButton.module.css';

const { PlusIcon } = lucideIcons;

type Props = {
    disabled?: boolean;
    onAdd: (pool: creatorbase.Pool) => void;
    /** The pool to show as picked, for a caller that holds on to its pick until the user acts on it. */
    value?: creatorbase.Pool | null;
    /** Replaces the "Add to Creator Pool" button, e.g. with one that names the picked pool. */
    trigger?: ReactElement;
    align?: 'start' | 'end';
    /** Adds a "Create new pool" entry under the list, which closes the popup and calls this. */
    onCreateNew?: () => void;
};

/** Stands in for the pools row while the account belongs to no pools. */
const AddToPoolButton = ({ disabled, onAdd, value, trigger, align = 'end', onCreateNew }: Props) => {
    const combobox = usePoolSearchCombobox({ onSelect: onAdd, value });

    return (
        <LibCombobox {...combobox.rootProps} disabled={disabled}>
            <LibCombobox.Trigger
                render={
                    trigger ?? (
                        <Button variant="outline" size="xs">
                            <PlusIcon /> Add to Creator Pool
                        </Button>
                    )
                }
            />

            <LibCombobox.Content align={align} className={styles.content}>
                <LibCombobox.Input placeholder="Search pools..." showTrigger={false} />
                <LibCombobox.Empty>{combobox.noItemsFound}</LibCombobox.Empty>
                <LibCombobox.List onScroll={combobox.onItemsListScroll}>
                    {(item: PoolItem) => (
                        <LibCombobox.Item key={item.value} value={item}>
                            <PoolOption
                                name={item.label}
                                description={item.description}
                                memberCount={item.member_count}
                                isMember={false}
                            />
                        </LibCombobox.Item>
                    )}
                </LibCombobox.List>
                {onCreateNew && (
                    <>
                        <LibCombobox.Separator />
                        <Button
                            variant="ghost"
                            size="xs"
                            className={styles.createPool}
                            onClick={() => {
                                combobox.rootProps.onOpenChange(false);
                                onCreateNew();
                            }}
                        >
                            <PlusIcon /> Create new pool
                        </Button>
                    </>
                )}
            </LibCombobox.Content>
        </LibCombobox>
    );
};

export default AddToPoolButton;
